<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {Icon} from '@iconify/vue';
import {storeToRefs} from 'pinia';
import {useMusicUploadStore} from '@/stores/useMusicUploadStore';
import {useNotificationStore} from '@/stores/notifications';
import {listMeasureNumbers} from '@/composables/musicXmlDom';
import TimeSignaturePicker from '@/components/time-signature/TimeSignaturePicker.vue';

// Tab „Taktart": Taktart ab einem Takt bis Stückende ändern, der Server verteilt die Noten neu
// (`POST /api/rebar`). Gleicher Schritt wie „Noten neu aufteilen" im Taktart-Editor des Takt-Menüs.
const uploadStore = useMusicUploadStore();
const notificationStore = useNotificationStore();
const {analysisResult, selectedFileContent, isRebarring, isPending} = storeToRefs(uploadStore);

const selectedBeats = ref(4);
const selectedBeatType = ref(4);
// Takt-INDEX, nicht Taktnummer – bei Auftakt hat Index 0 die Nummer 0.
const startIndex = ref(0);

const currentTimeSignature = computed(() => analysisResult.value?.time_signature ?? null);

// Vorbelegung mit der Taktart der Analyse, sobald eine neue Datei/ein neuer Stand analysiert ist.
watch(currentTimeSignature, (ts) => {
  const [beats, beatType] = (ts ?? '4/4').split('/').map(Number);
  if (beats && beatType) {
    selectedBeats.value = beats;
    selectedBeatType.value = beatType;
  }
}, {immediate: true});

const measureNumbers = computed<string[]>((oldValue) => {
  const next = selectedFileContent.value ? listMeasureNumbers(selectedFileContent.value) : [];
  // oldValue-Muster: gleiche Liste → gleiche Referenz (kein Neuaufbau der <option>s je Edit).
  if (oldValue && oldValue.length === next.length && oldValue.every((n, i) => n === next[i])) return oldValue;
  return next;
});

watch(measureNumbers, (numbers) => {
  if (startIndex.value >= numbers.length) startIndex.value = 0;
});

const canApply = computed(() => !!selectedFileContent.value && measureNumbers.value.length > 0 && !isPending.value);

async function applyRebar() {
  const result = await uploadStore.applyRebar({
    beats: selectedBeats.value,
    beatType: selectedBeatType.value,
    startMeasureIndex: startIndex.value,
  });
  if (result) {
    notificationStore.notify(`Taktart ${selectedBeats.value}/${selectedBeatType.value} angewendet.`, 'success', 2000);
  }
}
</script>

<template>
  <div class="max-w-full space-y-4">
    <p class="text-sm text-gray-600">
      Ändert die Taktart ab dem gewählten Takt bis zum Ende und verteilt die Noten neu –
      Noten über einer neuen Taktlinie werden geteilt und gebunden.
      <span v-if="currentTimeSignature" class="block mt-1 text-xs text-gray-500">
        Aktuell: <span class="font-mono">{{ currentTimeSignature }}</span>
      </span>
    </p>

    <TimeSignaturePicker v-model:beats="selectedBeats" v-model:beat-type="selectedBeatType"/>

    <label class="block text-sm font-medium text-gray-700">
      Ab Takt
      <select
          v-model.number="startIndex"
          class="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
          :disabled="!measureNumbers.length"
      >
        <option v-for="(number, index) in measureNumbers" :key="index" :value="index">
          {{ number }}{{ index === 0 ? ' (Anfang)' : '' }}
        </option>
      </select>
    </label>

    <button
        type="button"
        class="flex w-full items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="!canApply"
        @click="applyRebar"
    >
      <Icon
          :icon="isRebarring ? 'fa6-solid:spinner' : 'fa6-solid:scissors'"
          :class="isRebarring ? 'animate-spin' : ''"
      />
      Taktart ändern &amp; Noten neu aufteilen
    </button>
  </div>
</template>
